/**
 * WorkspaceMenuSettingsEditPanel
 *
 * 워크스페이스별 사이드바 메뉴를 활성/비활성하고 같은 depth 안에서 순서를 변경한다.
 * - 1depth 끼리만 순서 변경 가능
 * - 같은 부모를 가진 2depth 끼리만 순서 변경 가능
 * - 저장 시 전체 트리를 PUT으로 교체. 응답으로 Redux 메뉴 설정도 갱신.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Check, X as XIcon, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'

import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import type { WorkspaceMenuItemSchema } from '@/api/generated/models/WorkspaceMenuItemSchema'
import {
  resolveMenuTree,
  buildMenuSettingsPayload,
  type ResolvedMenuEntry,
} from '@/components/ui/Sidebar/mergeMenuSettings'
import { replaceCurrentWorkspaceMenuSettings } from '@/store/workspaceSlice'

interface Props {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  workspaceName: string
  /** workspace 응답에 포함된 menu_settings (없거나 빈 배열이면 카탈로그 기본값 사용) */
  initialMenuSettings: WorkspaceMenuItemSchema[] | null | undefined
  /** 저장 성공 시 호출. items는 백엔드 응답(정렬됨)을 그대로 넘김 */
  onSuccess?: (items: WorkspaceMenuItemSchema[]) => void
}

// ===== Sortable row components =====

interface ParentRowProps {
  entry: ResolvedMenuEntry
  childCount: number
  isActive: boolean
  onToggle: () => void
  children?: React.ReactNode
}

const ParentRow: React.FC<ParentRowProps> = ({ entry, childCount, isActive, onToggle, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `parent:${entry.catalog.key}`,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="flex items-center gap-2 py-2 px-2 rounded border"
        style={{
          borderColor: designTokens.colors.gray[1],
          backgroundColor: isActive ? designTokens.colors.white : designTokens.colors.gray[0],
        }}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="드래그하여 1depth 순서 변경"
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
        >
          <GripVertical className="w-4 h-4" style={{ color: designTokens.colors.gray[4] }} />
        </button>

        <ToggleButton isActive={isActive} onToggle={onToggle} ariaLabel={`${entry.catalog.label} 활성/비활성`} />

        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '15px',
            fontWeight: 600,
            color: designTokens.colors.gray[6],
            opacity: isActive ? 1 : 0.5,
          }}
        >
          {entry.catalog.label}
        </span>

        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            color: designTokens.colors.gray[4],
            marginLeft: '4px',
          }}
        >
          ({childCount}개 하위 항목)
        </span>
      </div>
      {/* 자식 슬롯 — 같은 부모의 children SortableContext */}
      <div className="pl-6 pt-2 space-y-2">{children}</div>
    </div>
  )
}

interface ChildRowProps {
  entry: ResolvedMenuEntry
  parentActive: boolean
  isActive: boolean
  onToggle: () => void
}

const ChildRow: React.FC<ChildRowProps> = ({ entry, parentActive, isActive, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `child:${entry.catalog.key}`,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  const dimmed = !parentActive || !isActive

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded border"
        style={{
          borderColor: designTokens.colors.gray[1],
          backgroundColor: designTokens.colors.white,
          opacity: dimmed ? 0.55 : 1,
        }}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="드래그하여 2depth 순서 변경"
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
          disabled={!parentActive}
        >
          <GripVertical className="w-3.5 h-3.5" style={{ color: designTokens.colors.gray[4] }} />
        </button>

        <ToggleButton
          isActive={isActive}
          onToggle={onToggle}
          ariaLabel={`${entry.catalog.label} 활성/비활성`}
          disabled={!parentActive}
        />

        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 400,
            color: designTokens.colors.gray[6],
          }}
        >
          {entry.catalog.label}
        </span>

        {entry.catalog.adminOnly && (
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              color: designTokens.colors.gray[4],
            }}
          >
            (관리자 전용)
          </span>
        )}
      </div>
    </div>
  )
}

const ToggleButton: React.FC<{
  isActive: boolean
  onToggle: () => void
  ariaLabel: string
  disabled?: boolean
}> = ({ isActive, onToggle, ariaLabel, disabled }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={ariaLabel}
    aria-pressed={isActive}
    disabled={disabled}
    className="flex items-center justify-center w-7 h-7 rounded border transition-colors"
    style={{
      borderColor: isActive ? designTokens.colors.success.DEFAULT : designTokens.colors.gray[2],
      backgroundColor: isActive ? designTokens.colors.success.light : designTokens.colors.white,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {isActive ? (
      <Check className="w-4 h-4" style={{ color: designTokens.colors.success.DEFAULT }} />
    ) : (
      <XIcon className="w-4 h-4" style={{ color: designTokens.colors.gray[4] }} />
    )}
  </button>
)

// ===== Main panel =====

interface EditTree {
  parent: ResolvedMenuEntry
  children: ResolvedMenuEntry[]
}

const WorkspaceMenuSettingsEditPanel: React.FC<Props> = ({
  isOpen,
  onClose,
  workspaceId,
  workspaceName,
  initialMenuSettings,
  onSuccess,
}) => {
  const dispatch = useAppDispatch()
  const { showError, showSuccess } = useAlert()

  // 권한 가드 (서버도 403으로 차단하지만 UI에서도 사전 차단)
  const orgMember = useAppSelector(state => state.auth.orgMember)
  const hasAdminPrivileges = orgMember?.role === 'owner' || orgMember?.role === 'admin'

  const [tree, setTree] = useState<EditTree[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 패널이 열릴 때마다 prop으로 받은 menu_settings로 편집 트리 초기화
  // (workspace 응답에 menu_settings가 포함되어 옴 — 별도 GET 없음)
  useEffect(() => {
    if (!isOpen || !workspaceId) {
      setTree([])
      return
    }
    // 편집 화면에서는 관리자 시점으로 모든 항목(adminOnly 포함) 노출
    setTree(resolveMenuTree(initialMenuSettings ?? null, true))
  }, [isOpen, workspaceId, initialMenuSettings])

  // ===== DnD sensors =====
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // ===== Drag handlers =====

  /** 1depth(parent) 순서 변경 */
  const handleParentDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const activeIdStr = String(active.id)
      const overIdStr = String(over.id)
      // 이벤트가 child의 drag일 수도 있으니 prefix로 필터
      if (!activeIdStr.startsWith('parent:') || !overIdStr.startsWith('parent:')) return

      setTree(prev => {
        const ids = prev.map(t => `parent:${t.parent.catalog.key}`)
        const oldIdx = ids.indexOf(activeIdStr)
        const newIdx = ids.indexOf(overIdStr)
        if (oldIdx < 0 || newIdx < 0) return prev
        return arrayMove(prev, oldIdx, newIdx)
      })
    },
    [],
  )

  /** 특정 parent 안의 2depth 순서 변경 */
  const makeChildDragEndHandler = useCallback(
    (parentKey: string) => (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const activeIdStr = String(active.id)
      const overIdStr = String(over.id)
      if (!activeIdStr.startsWith('child:') || !overIdStr.startsWith('child:')) return

      setTree(prev =>
        prev.map(t => {
          if (t.parent.catalog.key !== parentKey) return t
          const ids = t.children.map(c => `child:${c.catalog.key}`)
          const oldIdx = ids.indexOf(activeIdStr)
          const newIdx = ids.indexOf(overIdStr)
          if (oldIdx < 0 || newIdx < 0) return t
          return { ...t, children: arrayMove(t.children, oldIdx, newIdx) }
        }),
      )
    },
    [],
  )

  // ===== Toggles =====
  const toggleParent = (parentKey: string) => {
    setTree(prev =>
      prev.map(t =>
        t.parent.catalog.key === parentKey
          ? { ...t, parent: { ...t.parent, isActive: !t.parent.isActive } }
          : t,
      ),
    )
  }

  const toggleChild = (parentKey: string, childKey: string) => {
    setTree(prev =>
      prev.map(t =>
        t.parent.catalog.key === parentKey
          ? {
              ...t,
              children: t.children.map(c =>
                c.catalog.key === childKey ? { ...c, isActive: !c.isActive } : c,
              ),
            }
          : t,
      ),
    )
  }

  // ===== Save =====
  const handleSave = async () => {
    if (!hasAdminPrivileges) {
      showError('메뉴 설정을 변경할 권한이 없습니다.')
      return
    }
    setIsSubmitting(true)
    try {
      const items = buildMenuSettingsPayload(tree)
      // Redux thunk를 통해 호출 → 성공 시 store의 menuSettings 즉시 업데이트 → 사이드바 자동 갱신
      const result = await dispatch(
        replaceCurrentWorkspaceMenuSettings({ workspaceId, items }),
      )
      if (replaceCurrentWorkspaceMenuSettings.fulfilled.match(result)) {
        showSuccess('사이드바 메뉴 설정이 저장되었습니다.')
        onSuccess?.(result.payload.items)
        onClose()
      } else {
        // rejected — thunk 내부에서 메시지 가공됨
        const msg = typeof result.payload === 'string' ? result.payload : ErrorMessages.UPDATE_FAILED
        showError(msg)
      }
    } catch (err) {
      handleApiError(err, showError, ErrorMessages.UPDATE_FAILED)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ===== Render =====

  const parentIds = useMemo(() => tree.map(t => `parent:${t.parent.catalog.key}`), [tree])

  const headerActions = (
    <>
      <Button size="small" color="white" onClick={onClose} disabled={isSubmitting}>
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !hasAdminPrivileges || tree.length === 0}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={`사이드바 메뉴 설정 — ${workspaceName}`}
      width={640}
      headerActions={headerActions}
    >
      <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
        <p
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '13px',
            color: designTokens.colors.gray[5],
            lineHeight: 1.6,
          }}
        >
          좌측 손잡이를 드래그해 같은 depth 안에서 순서를 바꾸거나, 토글을 눌러 메뉴를 활성/비활성하세요.
          상위 메뉴를 비활성하면 하위 메뉴는 사이드바에서 함께 숨겨집니다.
        </p>

        {tree.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 메뉴가 없습니다.
            </span>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleParentDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext items={parentIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {tree.map(({ parent, children }) => {
                  const childIds = children.map(c => `child:${c.catalog.key}`)
                  return (
                    <ParentRow
                      key={parent.catalog.key}
                      entry={parent}
                      childCount={children.length}
                      isActive={parent.isActive}
                      onToggle={() => toggleParent(parent.catalog.key)}
                    >
                      {/* 같은 부모 안의 children만 묶는 별도 DndContext */}
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={makeChildDragEndHandler(parent.catalog.key)}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                      >
                        <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
                          {children.map(child => (
                            <ChildRow
                              key={child.catalog.key}
                              entry={child}
                              parentActive={parent.isActive}
                              isActive={child.isActive}
                              onToggle={() => toggleChild(parent.catalog.key, child.catalog.key)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </ParentRow>
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </SidePanel>
  )
}

export default WorkspaceMenuSettingsEditPanel
